import { captureException } from '@sentry/react'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IGroup } from '~/types/group'

import columns from './columns'

import ManageGroupModal from '../ManageGroupModal'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useTouchDown } from '~/hooks/useTouchDown'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useGroupsListQuery } from '~/queries/user-groups'
import { dateSort } from '~/utils/date'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const GroupListing = (): ReactElement => {
  /** Local state */

  const authUsersWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.AUTH.USERS.WRITE])

  const navigate = useNavigate()

  const gridRef = useRef<AgGridReact<IGroup>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false)

  const { isTouchDown, setIsTouchDown } = useTouchDown('Meta')

  /** Queries & Mutations */

  const { data: groups } = useGroupsListQuery()

  const sortGroups = groups?.data?.sort(({ updated_at_ts: aUpdatedDate }, { updated_at_ts: bUpdatedDate }) =>
    dateSort(aUpdatedDate, bUpdatedDate, 'desc')
  )

  /** Effects */

  useDocumentTitle('IAM Groups')

  /** Handlers */

  const handleRowClicked = (evt: RowClickedEvent) => {
    if (isTouchDown) {
      window.open(`/mwm-users/groups/${evt.data.id}`, '_blank', 'rel=noopener noreferrer')
      setIsTouchDown(false)
    } else {
      navigate(`/mwm-users/groups/${evt.data.id}`)
    }
  }

  const handleCreateGroupModalState = (isOpen: boolean) => () => setIsCreateGroupModalOpen(isOpen)

  const handleAddGroupsFailure = (error: unknown) => {
    captureException(error)
    displayToast('Group could not be created', 'error')
    setIsCreateGroupModalOpen(false)
  }

  const handleAddGroupsSuccess = () => {
    setIsCreateGroupModalOpen(false)
    displayToast('Group successfully created', 'success')
  }

  return (
    <>
      <Layout className="groups-view" withPadding>
        <ViewTitle className="layout-view-title">IAM Groups</ViewTitle>
        <ContentLayout>
          <TableWrapper
            addButton
            addButtonDisabled={!authUsersWriteAuthorized}
            addButtonLabel="New group"
            addButtonTooltip={!authUsersWriteAuthorized ? 'unauthorized' : undefined}
            className="groups-table with-min-height"
            columnDefs={columns}
            entriesName="groups"
            onAddButtonClick={handleCreateGroupModalState(true)}
            onRowClicked={handleRowClicked}
            ref={gridRef}
            rowData={sortGroups}
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </ContentLayout>
      </Layout>
      {authUsersWriteAuthorized ? (
        <ManageGroupModal
          isOpen={isCreateGroupModalOpen}
          onClose={handleCreateGroupModalState(false)}
          onFailure={handleAddGroupsFailure}
          onSuccess={handleAddGroupsSuccess}
        />
      ) : null}
    </>
  )
}
export default GroupListing
